import '../trailingSlash.js'
import './index.css'
import {
  parseTestMode,
  sanitizeParam,
  escapeHtml,
  showBrandLogo,
  hideBrandLogo,
  applyTestModeBadge as applyTestModeBadgeShared,
  applyBranding as applyBrandingShared
} from '../shared/branding.js'
const { buildPlanPresentation } = require('./planPresentation.js')
const { findExistingPurchasePlan, findPreviewOwnedPlan } = require('./catalogSelection.js')
const { renderUnlockCtaMarkup, UNLOCK_PREVIEW_STATES } = require('./unlockCta.js')
const { ICONS } = require('../shared/icons.js')
const {
  isFlowsPreview,
  flowsPreviewState,
  postFlowsEvent,
  observeFlowsSize
} = require('../shared/flows-preview.js')

const API_URL = 'https://api.payhook.link'
const DASHBOARD_ORIGIN = 'https://dashboard.payhook.link'

const FEATURE_ICON = {
  included: ICONS.checkCircle,
  locked: ICONS.lock,
  // What a locked row turns into while the CTA is hovered or focused.
  unlocked: ICONS.checkCircleSolid
}

const rootEl = document.getElementById('unlock-root')
const statusEl = document.getElementById('unlock-status')
const brandEl = document.getElementById('unlock-brand')
const brandNameEl = document.getElementById('unlock-brand-name')
const taglineEl = document.getElementById('unlock-tagline')
const featuresEl = document.getElementById('unlock-features')
const plansSectionEl = document.getElementById('unlock-plans-section')
const plansListEl = document.getElementById('unlock-plans')
const ctaEl = document.getElementById('unlock-cta')
const footnoteEl = document.getElementById('unlock-footnote')
const testBadgeEl = document.getElementById('unlock-test-badge')
const identityEl = document.getElementById('unlock-identity')
const identityEmailEl = document.getElementById('unlock-email-input')
const identityEditEl = document.getElementById('unlock-email-edit')
const ownedEl = document.getElementById('unlock-owned')
const ownedLeadEl = document.getElementById('unlock-owned-lead')
const ownedSubmitEl = document.getElementById('unlock-owned-submit')
const ownedStatusEl = document.getElementById('unlock-owned-status')
const promoEl = document.getElementById('unlock-promo')
const footerEl = document.querySelector('.site-footer')

// `?preview=flows`: rendered inside the admin consoles' Flows canvas. Same
// pixels as the real page, no side effects — see src/shared/flows-preview.js.
const flowsPreview = isFlowsPreview()

function setStatus (message, { error = false } = {}) {
  if (statusEl) {
    statusEl.textContent = message || ''
    statusEl.hidden = !message
  }
  rootEl.classList.toggle('unlock--error', error)
}

function setInlineStatus (el, message, variant) {
  el.textContent = message || ''
  el.hidden = !message
  el.classList.toggle('unlock__restore-status--error', variant === 'error')
  el.classList.toggle('unlock__restore-status--success', variant === 'success')
}

function applyTestModeBadge (testMode) {
  applyTestModeBadgeShared(rootEl, testBadgeEl, testMode, 'unlock--test-mode')
}

function buildUnlockUrl () {
  return window.location.href
}

function buildSuccessUrl ({ extensionId, paymentHookId, testMode }) {
  const url = new URL(`${window.location.origin}/success/`)
  if (extensionId) url.searchParams.set('extensionId', extensionId)
  if (paymentHookId) url.searchParams.set('paymentHookId', paymentHookId)
  if (testMode) url.searchParams.set('testMode', 'true')
  return url.toString()
}

function applyBranding (branding) {
  applyBrandingShared(branding, {
    rootEl,
    accentVarName: '--unlock-accent',
    accentSoftVarName: '--unlock-accent-soft',
    hasLogoClass: 'unlock--has-logo',
    applyLogo: (b) => showBrandLogo(b, { brandEl, brandNameEl }),
    applyNoLogo: (b) => hideBrandLogo(b, { brandEl, brandNameEl }),
    extra: (b) => {
      if (b.tagline) {
        taglineEl.textContent = b.tagline
        taglineEl.hidden = false
      }

      if (b.footerNote) {
        footnoteEl.textContent = b.footerNote
        footnoteEl.hidden = false
      }
    }
  })
}

function renderFeatures (features) {
  if (!Array.isArray(features) || features.length === 0) return

  const free = features.filter((f) => f.included)
  const pro = features.filter((f) => !f.included)
  const item = (f) => {
    if (f.included) {
      return `
        <div class="feature feature--included" role="listitem">
          <span class="feature__status" aria-hidden="true">
            <span class="feature__status-icon">${FEATURE_ICON.included}</span>
          </span>
          <span class="feature__label">${escapeHtml(f.label)}</span>
        </div>
      `
    }
    return `
      <div class="feature feature--locked" role="listitem">
        <span class="feature__status" aria-hidden="true">
          <span class="feature__status-icon feature__status-icon--lock">${FEATURE_ICON.locked}</span>
          <span class="feature__status-icon feature__status-icon--unlock">${FEATURE_ICON.unlocked}</span>
        </span>
        <span class="feature__label">${escapeHtml(f.label)}</span>
      </div>
    `
  }

  let html = ''
  if (free.length > 0) {
    html += `<div class="unlock__features-tier unlock__features-tier--free">${free.map(item).join('')}</div>`
  }
  if (pro.length > 0) {
    html += `<div class="unlock__features-tier unlock__features-tier--pro">${pro.map(item).join('')}</div>`
  }

  featuresEl.innerHTML = html
  featuresEl.hidden = false
}

function planCardName (plan) {
  if (plan.type === 'one_time') return 'Lifetime'
  if (plan.interval === 'year') return 'Annual'
  if (plan.interval === 'month') return 'Monthly'
  if (plan.interval === 'week') return 'Weekly'
  return plan.productName || 'Plan'
}

function isRecommendedPlan (plan, plans) {
  if (plans.length === 1) return false
  if (plan.interval === 'year') return true
  return false
}

function renderPlanCards (plans, selectedPriceId) {
  return plans.map((plan) => {
    const recommended = isRecommendedPlan(plan, plans)
    const selected = plan.priceId === selectedPriceId
    const presentation = buildPlanPresentation(plan, plans)
    const badge = recommended
      ? `Best value${presentation.badge ? ` · ${presentation.badge}` : ''}`
      : presentation.badge
    return `
      <div role="listitem">
        <button
          type="button"
          class="plan-card ${recommended ? 'plan-card--recommended' : ''} ${selected ? 'plan-card--selected' : ''}"
          data-price-id="${escapeHtml(plan.priceId)}"
          aria-pressed="${selected ? 'true' : 'false'}"
        >
          <span class="plan-card__radio" aria-hidden="true">${ICONS.check}</span>
          <span class="plan-card__body">
            <span class="plan-card__header">
              <span class="plan-card__name">${escapeHtml(planCardName(plan))}</span>
              ${badge ? `<span class="plan-card__badge">${escapeHtml(badge)}</span>` : ''}
            </span>
            <span class="plan-card__price">${escapeHtml(plan.label)}</span>
            ${presentation.summary ? `<span class="plan-card__value">${escapeHtml(presentation.summary)}</span>` : ''}
          </span>
        </button>
      </div>
    `
  }).join('')
}

async function fetchUnlock ({ paymentHookId, version, testMode, email }) {
  const headers = {}
  if (testMode) headers['Test-Mode'] = 'True'

  const params = new URLSearchParams()
  params.set('paymentHookId', paymentHookId)
  if (version) params.set('version', version)
  if (email) params.set('email', email)
  // Same catalog (plans, branding, ownership), but a preview must never be
  // counted as a pricing-experiment exposure.
  if (flowsPreview) params.set('purpose', 'branding')

  const response = await fetch(
    `${API_URL}/unlock?${params.toString()}`,
    { headers }
  )
  const data = await response.json()
  if (!response.ok) {
    throw new Error(data?.error?.message || `Could not load plans (${response.status})`)
  }
  return data
}

function fetchDashboardPreviewCatalog (paymentHookId, requestId) {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      window.removeEventListener('message', receivePreviewCatalog)
      reject(new Error('Could not load dashboard preview data.'))
    }, 15000)

    function receivePreviewCatalog (event) {
      if (event.origin !== DASHBOARD_ORIGIN || event.source !== window.parent) return
      const message = event.data
      if (
        !message ||
        message.type !== 'payhook:unlock-preview-catalog' ||
        message.paymentHookId !== paymentHookId ||
        message.requestId !== requestId ||
        !message.catalog ||
        !Array.isArray(message.catalog.plans)
      ) return

      window.clearTimeout(timeout)
      window.removeEventListener('message', receivePreviewCatalog)
      resolve(message.catalog)
    }

    window.addEventListener('message', receivePreviewCatalog)
    window.parent.postMessage({
      type: 'payhook:unlock-preview-ready',
      paymentHookId,
      requestId
    }, DASHBOARD_ORIGIN)
  })
}

function createUnlockButton ({ plan, accountId, paymentHookId, testMode, extensionId, promoId }) {
  return new window.Payhook.UnlockButton({
    paymentHookId,
    productId: plan.productId,
    priceId: plan.priceId,
    accountId,
    // Applied to the Checkout Session by the API. The client that opened this
    // page decides whether the offer is on; the account decides what it is.
    promoId,
    cancelUrl: buildUnlockUrl(),
    successUrl: buildSuccessUrl({ extensionId, paymentHookId, testMode }),
    testMode
  })
}

// Shown only when the caller passes a promo: an offer nobody qualified for
// must never appear as a discount that then fails to apply at checkout.
function applyPromo ({ promoId, promoLabel }) {
  if (!promoId) {
    promoEl.hidden = true
    return
  }
  // promoLabel comes from the URL, so it only ever reaches the DOM as text.
  const label = document.createElement('span')
  label.textContent = promoLabel
    ? `${promoLabel} applied at checkout`
    : 'Your discount is applied at checkout'
  promoEl.innerHTML = ICONS.tag
  promoEl.append(label)
  promoEl.hidden = false
}

async function mountUnlockButton ({ plan, accountId, paymentHookId, email, testMode, extensionId, promoId }) {
  const root = document.getElementById('payhook-button')
  root.innerHTML = ''
  const unlockButton = createUnlockButton({ plan, accountId, paymentHookId, testMode, extensionId, promoId })
  await unlockButton.init('payhook-button', { email })
  const button = root.querySelector('#unlock')
  if (button) button.textContent = `Unlock ${planCardName(plan)} · ${plan.label}`
  bindPreviewUnlock()
}

// Flows preview stand-in for mountUnlockButton: the markup UnlockButton.init
// renders, with the same label and hover binding, but no UnlockButton — so no
// `button_load` tracking and no payment on click.
function renderStaticUnlockButton ({ plan, email }) {
  const root = document.getElementById('payhook-button')
  root.innerHTML = renderUnlockCtaMarkup({ email })
  const button = root.querySelector('#unlock')
  if (button) {
    button.textContent = `Unlock ${planCardName(plan)} · ${plan.label}`
    button.addEventListener('click', () => {
      postFlowsEvent('checkout-click', { priceId: plan.priceId })
    })
  }
  bindPreviewUnlock()
}

// Fallback for browsers without :has() — toggle .unlock--preview when the
// CTA button is hovered or focused. Idempotent: safe to call after re-mount.
function bindPreviewUnlock () {
  const button = ctaEl.querySelector('button')
  if (!button || button.dataset.previewBound === 'true') return
  button.dataset.previewBound = 'true'

  const setPreview = (on) => rootEl.classList.toggle('unlock--preview', on)
  button.addEventListener('mouseenter', () => setPreview(true))
  button.addEventListener('mouseleave', () => setPreview(false))
  button.addEventListener('focus', () => setPreview(true))
  button.addEventListener('blur', () => setPreview(false))
}

async function init () {
  const params = new URLSearchParams(window.location.search)
  const paymentHookId = sanitizeParam(params.get('paymentHookId'))
  const extensionId = sanitizeParam(params.get('extensionId'))
  const email = sanitizeParam(params.get('email'))
  const testMode = parseTestMode(sanitizeParam(params.get('testMode')))
  const version = sanitizeParam(params.get('version'))
  const promoId = sanitizeParam(params.get('promoId'))
  const promoLabel = sanitizeParam(params.get('promoLabel'))
  const dashboardPreview = !flowsPreview && params.get('preview') === 'dashboard'
  const previewState = flowsPreviewState(window.location.search, UNLOCK_PREVIEW_STATES)
  const previewRequestId = sanitizeParam(params.get('previewRevision'))

  applyTestModeBadge(testMode)
  observeFlowsSize(rootEl, { include: [footerEl] })

  if (!paymentHookId) {
    setStatus('Missing payment hook. Open unlock from your extension.', { error: true })
    return
  }

  let catalog
  try {
    catalog = dashboardPreview
      ? await fetchDashboardPreviewCatalog(paymentHookId, previewRequestId)
      : await fetchUnlock({ paymentHookId, version, testMode, email })
  } catch (error) {
    setStatus(error.message || 'Could not load plans.', { error: true })
    return
  }

  applyBranding(catalog.branding)
  applyPromo({ promoId, promoLabel })

  const plans = Array.isArray(catalog.plans) ? catalog.plans : []
  if (plans.length === 0) {
    setStatus('No plans are available yet.', { error: true })
    return
  }

  if (!catalog.accountId) {
    setStatus('Checkout is not configured for this account.', { error: true })
    return
  }

  renderFeatures(catalog.branding && catalog.branding.features)

  setStatus(null)
  rootEl.classList.add('unlock--ready')

  let selectedPlan = plans.find((p) => isRecommendedPlan(p, plans)) || plans[0]
  let mountGeneration = 0
  const multiPlan = plans.length > 1

  function selectExistingPurchasePlan (unlockCatalog) {
    const existingPlan = findExistingPurchasePlan(plans, unlockCatalog)
    if (!existingPlan) return false
    selectedPlan = existingPlan
    return true
  }

  // Identity: the known/entered email. Feeds whichever of the buy flow or
  // the owned-panel restore action the customer ends up using — there's
  // only one email field on the page, and editing it re-checks entitlement
  // (see recheckEmail below) rather than needing a separate manual action.
  identityEmailEl.value = email || ''
  if (email && email.includes('@')) {
    identityEmailEl.readOnly = true
    identityEditEl.hidden = false
  }
  identityEl.hidden = false

  identityEditEl.addEventListener('click', () => {
    identityEmailEl.readOnly = false
    identityEmailEl.focus()
    identityEmailEl.select()
    identityEditEl.hidden = true
  })

  function findPlanForCard (card) {
    const priceId = card.getAttribute('data-price-id')
    return plans.find((p) => p.priceId === priceId)
  }

  function updatePlanSelection (plan) {
    selectedPlan = plan
    if (!multiPlan) return

    plansListEl.querySelectorAll('.plan-card').forEach((card) => {
      const isSelected = card.getAttribute('data-price-id') === plan.priceId
      card.classList.toggle('plan-card--selected', isSelected)
      card.setAttribute('aria-pressed', isSelected ? 'true' : 'false')
    })
    rootEl.classList.add('unlock--plan-selected')
  }

  function setCtaLoading (loading) {
    ctaEl.classList.toggle('unlock__cta--loading', loading)
  }

  const selectPlan = async (plan) => {
    updatePlanSelection(plan)
    const generation = ++mountGeneration
    setStatus(null)

    if (flowsPreview) {
      renderStaticUnlockButton({ plan, email: identityEmailEl.value.trim() })
      return
    }

    setCtaLoading(true)

    if (!window.Payhook?.UnlockButton) {
      setCtaLoading(false)
      setStatus('Checkout failed to load. Refresh and try again.', { error: true })
      return
    }

    try {
      await mountUnlockButton({
        plan,
        accountId: catalog.accountId,
        paymentHookId,
        email: identityEmailEl.value.trim(),
        testMode,
        extensionId,
        promoId
      })
      if (generation !== mountGeneration) return
      setStatus(null)
    } catch (error) {
      console.error(error)
      if (generation !== mountGeneration) return
      setStatus('Checkout failed to load. Refresh and try again.', { error: true })
    } finally {
      if (generation === mountGeneration) setCtaLoading(false)
    }
  }

  if (multiPlan) {
    plansListEl.addEventListener('click', (event) => {
      const card = event.target.closest('.plan-card')
      if (!card) return
      const plan = findPlanForCard(card)
      if (!plan) return
      if (flowsPreview) postFlowsEvent('plan-select', { priceId: plan.priceId })
      selectPlan(plan)
    })
  }

  const start = () => selectPlan(selectedPlan)

  // Reveals the plan picker + buy CTA and mounts the checkout button. Called
  // right away for a customer with no known existing purchase, or as a
  // fallback if a "already have access" attempt below turns out to be wrong.
  function startBuyFlow () {
    ctaEl.hidden = false
    if (multiPlan) {
      plansSectionEl.hidden = false
      plansListEl.innerHTML = renderPlanCards(plans, selectedPlan.priceId)
      rootEl.classList.add('unlock--multi-plan')
    }

    if (flowsPreview || window.Payhook?.UnlockButton) {
      start()
    } else {
      const unlockScript = document.getElementById('payhook-unlock-button')
      unlockScript.addEventListener('load', () => { start() })
    }
  }

  // Sends the actual unlock email for the auto-detected "already have access"
  // panel. An existing entitlement is resolved against a submitted
  // product+price (see resolvePaymentChallenge server side), but for a
  // returning customer the server ignores that price and resolves the one
  // they actually bought — so any plan on this hook's catalog works as the
  // throwaway target here. Returns true if an unlock email was sent, false
  // if the email has no matching purchase (onNotFound then runs).
  async function sendRestoreEmail (targetEmail, { onNotFound } = {}) {
    if (!window.Payhook?.UnlockButton) {
      throw new Error('Still loading — try again in a moment.')
    }

    const restoreButton = createUnlockButton({
      plan: selectedPlan,
      accountId: catalog.accountId,
      paymentHookId,
      testMode,
      extensionId
    })
    const result = await restoreButton.restore({ email: targetEmail })

    if (result.challengeType === 'email_link') return true
    if (onNotFound) onNotFound()
    return false
  }

  // Skip the plan picker entirely — a known email that already resolves to
  // an email-link challenge gets this one visible action instead of having
  // to pick a plan just to trigger it.
  function applyOwnedState () {
    ctaEl.hidden = true
    plansSectionEl.hidden = true
    setInlineStatus(ownedStatusEl, null)
    ownedLeadEl.textContent = 'You already have access.'
    ownedEl.hidden = false
    ownedSubmitEl.disabled = false
    ownedSubmitEl.textContent = 'Unlock'
  }

  function applyBuyState () {
    ownedEl.hidden = true
    setInlineStatus(ownedStatusEl, null)
    startBuyFlow()
  }

  ownedSubmitEl.addEventListener('click', async () => {
    if (flowsPreview) {
      // Shows the post-send state without sending the restore email.
      postFlowsEvent('restore-click', { priceId: selectedPlan.priceId })
      setInlineStatus(ownedStatusEl, null)
      ownedSubmitEl.disabled = true
      ownedSubmitEl.textContent = 'Check your inbox'
      return
    }

    const targetEmail = identityEmailEl.value.trim()
    setInlineStatus(ownedStatusEl, null)
    ownedSubmitEl.disabled = true
    ownedSubmitEl.textContent = 'Sending…'

    try {
      const found = await sendRestoreEmail(targetEmail, { onNotFound: applyBuyState })
      if (found) ownedSubmitEl.textContent = 'Check your inbox'
    } catch (error) {
      console.error(error)
      setInlineStatus(ownedStatusEl, error.message || 'Something went wrong. Please try again.', 'error')
      ownedSubmitEl.disabled = false
      ownedSubmitEl.textContent = 'Unlock'
    }
  })

  // Re-checks entitlement (read-only — never sends an email on its own) once
  // editing the email field is "done" (blur, or Enter), and switches between
  // the owned panel and the plan picker accordingly. Skips the network round
  // trip if the value hasn't actually changed since the last check.
  let lastCheckedEmail = (email || '').trim().toLowerCase()

  async function recheckEmail () {
    const value = identityEmailEl.value.trim()
    if (!value || !value.includes('@')) return
    const normalized = value.toLowerCase()
    if (normalized === lastCheckedEmail) return
    lastCheckedEmail = normalized

    let updated
    try {
      updated = await fetchUnlock({ paymentHookId, version, testMode, email: value })
    } catch (error) {
      console.error(error)
      return
    }

    if (selectExistingPurchasePlan(updated)) {
      applyOwnedState()
    } else {
      applyBuyState()
    }
  }

  identityEmailEl.addEventListener('blur', () => { recheckEmail() })
  identityEmailEl.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      identityEmailEl.blur()
    }
  })

  if (previewState === 'owned') {
    selectedPlan = findPreviewOwnedPlan(plans, catalog)
    applyOwnedState()
  } else if (previewState === 'buy') {
    startBuyFlow()
  } else if (selectExistingPurchasePlan(catalog)) {
    applyOwnedState()
  } else {
    startBuyFlow()
  }
}

init().catch((error) => {
  console.error(error)
  setStatus('Something went wrong. Refresh and try again.', { error: true })
})
